import { OPTION_LABEL_MAX, SURVEY_OPTIONS_MAX, SURVEY_OPTIONS_MIN, type AnswerType, type SurveyInput } from '@zoxo/shared';
import { useState } from 'react';
import { CircleX, Plus, X } from '@/components/icons';

import { ConfirmDialog } from '@/components/ui/confirm-dialog';

import { Switch } from '../../components/ui/switch';

/**
 * 글에 붙일 설문 — 툴바의 「설문 추가」가 연다. 보기 2~20개, 단일/복수 선택.
 * 편집기 본문 밖의 일이라 tiptap 노드가 아니다. 글과 함께 한 번에 저장된다(createPostSchema.survey).
 */
export const EMPTY_SURVEY: SurveyInput = { answerType: 'single', options: [{ label: '' }, { label: '' }] };

const ANSWER_LABEL: Record<AnswerType, string> = { single: '1개만 선택 가능한 설문', multiple: '여러 개 선택 가능한 설문' };

export function SurveyBuilder({ value, onChange, onRemove }: { value: SurveyInput; onChange: (v: SurveyInput) => void; onRemove: () => void }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const setOption = (i: number, label: string) =>
    onChange({ ...value, options: value.options.map((o, j) => (j === i ? { label } : o)) });
  const addOption = () => {
    if (value.options.length >= SURVEY_OPTIONS_MAX) return;
    onChange({ ...value, options: [...value.options, { label: '' }] });
  };
  const removeOption = (i: number) => {
    if (value.options.length <= SURVEY_OPTIONS_MIN) return;
    onChange({ ...value, options: value.options.filter((_, j) => j !== i) });
  };

  return (
    // 네모 상자가 아니라 게시판 칸과 같은 「위 가로선 한 줄」 구획.
    <div className="mt-8 border-t border-[#e3d8bf] pt-8">
      <div className="mb-4 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-text">설문</span>
        {/* 바로 지우지 않는다 — 한 번 더 묻는다(이미지 삭제와 같은 확인창). */}
        <button type="button" onClick={() => setConfirmOpen(true)} className="text-sm text-text-muted hover:text-danger" aria-label="설문 제거">
          설문 제거
        </button>
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            onRemove();
          }}
          title="정말로 설문을 제거할까요?"
          description="적어 둔 보기가 함께 사라집니다."
          confirmLabel="예"
          cancelLabel="아니오"
          destructive
        />
      </div>
      {/* 답변 방식 — 스위치 하나. 꺼짐 = 1개만 선택, 켜짐 = 여러 개 선택. */}
      <label className="mb-5 flex items-center gap-2.5 text-sm text-text">
        <Switch
          checked={value.answerType === 'multiple'}
          onCheckedChange={(on) => onChange({ ...value, answerType: on ? 'multiple' : 'single' })}
          aria-label="여러 개 선택 가능한 설문"
        />
        <span>{ANSWER_LABEL[value.answerType]}</span>
      </label>
      <ol className="space-y-2">
        {value.options.map((o, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="w-5 shrink-0 text-right text-xs tabular-nums text-text-subtle">{i + 1}</span>
            <input
              type="text"
              value={o.label}
              maxLength={OPTION_LABEL_MAX}
              placeholder={`보기 ${i + 1}`}
              onChange={(e) => setOption(i, e.target.value)}
              className="h-9 min-w-0 flex-1 rounded-lg border border-border bg-surface px-3 text-sm text-text outline-none focus:border-gray-400"
            />
            <button
              type="button"
              onClick={() => removeOption(i)}
              disabled={value.options.length <= SURVEY_OPTIONS_MIN}
              aria-label={`보기 ${i + 1} 삭제`}
              className="group inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-text-muted hover:text-text disabled:opacity-30"
            >
              {/* 마우스를 올리면 X 가 동그라미 X 로 바뀐다. */}
              <X className="size-4 group-hover:hidden" />
              <CircleX className="hidden size-4 group-hover:block" />
            </button>
          </li>
        ))}
      </ol>
      {/* 보기 추가 — 보기 입력 칸과 같은 자리·같은 높이의 점선 상자. */}
      <button
        type="button"
        onClick={addOption}
        disabled={value.options.length >= SURVEY_OPTIONS_MAX}
        className="mt-4 ml-7 flex h-9 w-[calc(100%-1.75rem-2.75rem)] items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 bg-white text-sm text-text-muted transition-colors hover:border-gray-400 hover:text-text disabled:opacity-30"
      >
        <Plus className="size-3.5" />
        보기 추가
      </button>
    </div>
  );
}

/** 저장 가능한가 — 보기가 전부 채워졌는가. */
export function surveyReady(s: SurveyInput | null): boolean {
  return s === null || s.options.every((o) => o.label.trim().length > 0);
}
